import type { FormSummary } from './types.ts';

// ── HTML escaping ────────────────────────────────────────────────
export function esc(s: unknown): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Relative time formatting ─────────────────────────────────────
export function relTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return Math.floor(diff / 60) + 'm ago';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h ago';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd ago';
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}

// ── Card thumbnail strip pattern ──────────────────────────────────
export function thumbLines(): string {
  const patterns = [
    ['w-full', 'w-75 sm', 'w-90 sm', 'w-full', 'w-60 sm sm', 'w-80 sm', 'w-50', 'w-40 sm sm'],
    ['w-75', 'w-full sm', 'w-50', 'w-90 sm', 'w-full', 'w-60 sm', 'w-80 sm sm', 'w-40 sm'],
    ['w-90', 'w-50 sm', 'w-full', 'w-75 sm', 'w-60', 'w-80 sm sm', 'w-full sm', 'w-50 sm'],
  ];
  const p = patterns[Math.floor(Math.random() * patterns.length)];
  return p
    .slice(0, 7)
    .map((c) => `<div class="fc-thumb-line ${c}"></div>`)
    .join('');
}

// ── Render grid ────────────────────────────────────────────────
export function renderGrid(grid: HTMLElement, forms: FormSummary[]): void {
  grid.innerHTML = '';

  if (!forms.length) {
    grid.innerHTML = `<div class="empty-state" style="grid-column:1/-1">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
      <p>No forms yet.<br><strong>Click "+ Blank form"</strong> above to create your first form.</p>
    </div>`;
    return;
  }

  forms.forEach((f) => {
    const card = document.createElement('div');
    card.className = 'form-card';
    card.dataset.id = f.id;

    const respChip =
      f.responseCount > 0
        ? `<span class="fc-resp-chip">🗂 ${f.responseCount} response${f.responseCount !== 1 ? 's' : ''}</span>`
        : '';

    card.innerHTML = `
      <div class="fc-thumb" data-open="${f.id}">${thumbLines()}</div>
      <button class="fc-menu-btn" title="More options" data-menu="${f.id}">⋮</button>
      <div class="fc-dropdown" id="dd-${f.id}">
        <div class="fc-dd-item" data-action="open" data-id="${f.id}"><span class="dd-icon">✏️</span> Open</div>
        <div class="fc-dd-item" data-action="rename" data-id="${f.id}"><span class="dd-icon">🏷️</span> Rename</div>
        <div class="fc-dd-item" data-action="responses" data-id="${f.id}"><span class="dd-icon">📊</span> View responses</div>
        <div class="fc-dd-item danger" data-action="delete" data-id="${f.id}"><span class="dd-icon">🗑️</span> Delete</div>
      </div>
      <div class="fc-info">
        <div class="fc-title" title="${esc(f.title)}">${esc(f.title) || 'Untitled Form'}</div>
        <div class="fc-meta">
          <span class="icon">📋</span>
          <span>Opened ${relTime(f.updatedAt)}</span>
          ${respChip}
        </div>
      </div>`;
    grid.appendChild(card);
  });
}

// ── Sort ───────────────────────────────────────────────────────
export function sortedForms(forms: FormSummary[], key: string): FormSummary[] {
  const cp = [...forms];
  if (key === 'title') cp.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  else if (key === 'responses') cp.sort((a, b) => b.responseCount - a.responseCount);
  else cp.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  return cp;
}
